import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { getStoryblokApi } from "@storyblok/react";
import type {
  GlobalStoryblok,
  NavMenuItemStoryblok,
  LinkItemStoryblok,
} from "../types/global";

type GlobalContentValue = {
  menuItems: NavMenuItemStoryblok[];
  socials: LinkItemStoryblok[];
  phone?: string;
  email?: string;
  address?: string;
  openingHours?: string;
};

const defaultValue: GlobalContentValue = {
  menuItems: [],
  socials: [],
};

const GlobalContext = createContext<GlobalContentValue>(defaultValue);
const STORYBLOK_VERSION = (process.env.REACT_APP_STORYBLOK_VERSION ||
  "draft") as "draft" | "published";

export const GlobalProvider = ({ children }: { children: ReactNode }) => {
  const [content, setContent] = useState<GlobalStoryblok | null>(null);

  useEffect(() => {
    const storyblokApi = getStoryblokApi();
    storyblokApi
      .get("cdn/stories/globals", {
        version: STORYBLOK_VERSION,
        resolve_links: "url",
      })
      .then(({ data }: any) => setContent(data.story.content))
      .catch(() => {
        // Nog geen "globals"-story in Storyblok (of tijdelijk niet bereikbaar);
        // val terug op lege navigatie/contactgegevens i.p.v. de app te breken.
        setContent(null);
      });
  }, []);

  const value: GlobalContentValue = {
    menuItems: content?.menu_items ?? [],
    socials: content?.socials ?? [],
    phone: content?.telefoon,
    email: content?.email,
    address: content?.address,
    openingHours: content?.opening_hours,
  };

  return (
    <GlobalContext.Provider value={value}>{children}</GlobalContext.Provider>
  );
};

export function useGlobalContent() {
  return useContext(GlobalContext);
}
